import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Checkbox } from '@/components/ui/checkbox';
import { CheckSquare, AlertTriangle, Info } from 'lucide-react';
import { RecommendationDoc } from '@/dao/firestore/FeedbackSummaryRepository';
import { useTranslation } from 'react-i18next';

interface RecommendationsModalProps {
  open: boolean;  
  onClose: () => void;
  recommendations: RecommendationDoc[];
}

const priorityConfig = {
  high: { 
    color: 'bg-red-500', 
    textColor: 'text-red-600',
    bgColor: 'bg-red-50 dark:bg-red-950',
    borderColor: 'border-red-200 dark:border-red-800',
    icon: AlertTriangle
  },
  medium: { 
    color: 'bg-yellow-500', 
    textColor: 'text-yellow-600',
    bgColor: 'bg-yellow-50 dark:bg-yellow-950',
    borderColor: 'border-yellow-200 dark:border-yellow-800',
    icon: Info
  },
  low: { 
    color: 'bg-green-500', 
    textColor: 'text-green-600',
    bgColor: 'bg-green-50 dark:bg-green-950',
    borderColor: 'border-green-200 dark:border-green-800',
    icon: CheckSquare
  },
};

export default function RecommendationsModal({ open, onClose, recommendations }: RecommendationsModalProps) {
  const { t } = useTranslation();
  const [completedItems, setCompletedItems] = useState<Set<number>>(new Set());

  const progress = recommendations.length > 0 ? (completedItems.size / recommendations.length) * 100 : 0;

  const toggleCompletion = (index: number) => {
    const newCompleted = new Set(completedItems);
    if (newCompleted.has(index)) {
      newCompleted.delete(index);
    } else {
      newCompleted.add(index);
    }
    setCompletedItems(newCompleted);
  };

  const getPriorityLabel = (priority: string) => {
    switch (priority) {
      case 'high': return t('screeningRoom.recommendations.impact.high');
      case 'medium': return t('screeningRoom.recommendations.impact.medium');
      case 'low': return t('screeningRoom.recommendations.impact.low');
      default: return priority;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CheckSquare className="h-5 w-5" />
            {t('screeningRoom.recommendations.title')}
          </DialogTitle>
        </DialogHeader>

        {/* Progress Bar */}
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">{t('screeningRoom.recommendations.progress')}</span>
            <span className="font-medium">{completedItems.size}/{recommendations.length}</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        {/* Recommendations List */}
        <div className="flex-1 overflow-y-auto space-y-4 pr-2">
          {recommendations.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              {t('screeningRoom.recommendations.noRecommendations')}
            </div>
          ) : (
            recommendations.map((recommendation, index) => {
              const config = priorityConfig[recommendation.priority];
              const Icon = config.icon;
              const isCompleted = completedItems.has(index);

              return (
                <div
                  key={index}
                  className={`p-4 rounded-lg border transition-all ${
                    isCompleted ? 'opacity-60 bg-muted/50' : config.bgColor
                  } ${config.borderColor}`}
                >
                  <div className="flex items-start gap-3">
                    <Checkbox
                      checked={isCompleted}
                      onCheckedChange={() => toggleCompletion(index)}
                      className="mt-1"
                    />
                    
                    <div className="flex-1 space-y-3">
                      {/* Priority Badge */}
                      <div className="flex items-center gap-2">
                        <Badge 
                          variant="secondary" 
                          className={`${config.color} text-white`}
                        >
                          <Icon className="h-3 w-3 mr-1" />
                          {getPriorityLabel(recommendation.priority)} {t('screeningRoom.recommendations.impactLabel')}
                        </Badge>
                      </div>

                      {/* Comment */}
                      <p className={`text-sm leading-relaxed ${
                        isCompleted ? 'line-through text-muted-foreground' : ''
                      }`}>
                        {recommendation.comment}
                      </p>

                      {/* Personas */}
                      {recommendation.personas.length > 0 && (
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-muted-foreground">
                            {t('screeningRoom.recommendations.suggestedBy')}:
                          </span>
                          <div className="flex -space-x-2">
                            {recommendation.personas.slice(0, 4).map((persona, personaIndex) => {
                              const personaName = typeof persona === 'string' ? persona : (persona.name || '');
                              const personaId = typeof persona === 'string' ? persona : (persona.id || personaIndex);
                              const personaImage = typeof persona === 'string' ? undefined : persona.image;
                              return (
                              <Avatar key={personaId} className="h-6 w-6 border-2 border-background">
                                <AvatarImage src={personaImage} alt={personaName} />
                                <AvatarFallback className="text-xs">
                                  {personaName.charAt(0) || '?'}
                                </AvatarFallback>
                              </Avatar>
                              );
                            })}
                            {recommendation.personas.length > 4 && (
                              <div className="h-6 w-6 rounded-full bg-muted border-2 border-background flex items-center justify-center">
                                <span className="text-xs text-muted-foreground">
                                  +{recommendation.personas.length - 4}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}